import React, { useEffect, useMemo, useState } from 'react';
import StudioPage from './StudioPage';
import { STUDIO_PRESETS } from './data/presets';
import { createProject } from './utils/studioProjectRepo';
import useFranchiseeWorkspace from './utils/useFranchiseeWorkspace';
import DesignNameModal from './components/DesignNameModal';
import CustomSizeModal from './components/CustomSizeModal';
import './styles/StudioHome.css';

const formatDateLabel = (date) => (
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
);

const CATEGORY_OPTIONS = [
  'All',
  'Instagram',
  'WhatsApp',
  'Facebook',
  'YouTube',
  'LinkedIn',
  'X',
  'Print',
  'Real Estate',
];

const QUICK_SIZES = [
  { width: 1080, height: 1080 },
  { width: 1080, height: 1350 },
  { width: 1080, height: 1920 },
  { width: 1200, height: 630 },
  { width: 1280, height: 720 },
  { width: 2480, height: 3508 },
];

const StudioHome = ({
  firebase,
  authUser,
}) => {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [createError, setCreateError] = useState('');
  const [creatingPresetId, setCreatingPresetId] = useState(null);
  const { franchiseeId, uid, loading: workspaceLoading } =
    useFranchiseeWorkspace();
  const [pendingPreset, setPendingPreset] = useState(null);
  const [designName, setDesignName] = useState('');
  const [designNameError, setDesignNameError] = useState('');
  const [recentDesigns, setRecentDesigns] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [debugError, setDebugError] = useState('');
  const [openNotice, setOpenNotice] = useState('');
  const [blockedUrl, setBlockedUrl] = useState('');
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [customUnit, setCustomUnit] = useState('px');
  const [customName, setCustomName] = useState('');
  const [customErrors, setCustomErrors] = useState({});
  const [customNameTouched, setCustomNameTouched] = useState(false);
  const [deletingDesignId, setDeletingDesignId] = useState('');
  const [storageUsedBytes, setStorageUsedBytes] = useState(0);

  const STORAGE_LIMIT_BYTES = 1024 * 1024 * 1024;

  const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${Math.round(bytes)} B`;
  };

  const measureJsonBytes = (value) => {
    try {
      const json = JSON.stringify(value || {});
      if (typeof TextEncoder !== 'undefined') {
        return new TextEncoder().encode(json).length;
      }
      return json.length;
    } catch (_err) {
      return 0;
    }
  };

  const openEditorInNewTab = (projectId) => {
    const url = `/studio-editor/${projectId}`;
    const newTab = window.open(url, '_blank', 'noopener,noreferrer');
    if (newTab) {
      setOpenNotice('Opened editor in a new tab.');
      setBlockedUrl('');
      try {
        newTab.focus();
      } catch (error) {
        // Ignore focus errors in restricted contexts.
      }
    } else {
      setOpenNotice('Popup blocked. Use the button below to open the editor.');
      setBlockedUrl(url);
    }
  };

  const handleDeleteDesign = async (event, design) => {
    event.preventDefault();
    event.stopPropagation();
    if (!firebase || !franchiseeId || !design?.id) return;
    const confirmed = window.confirm(
      `Delete "${design.name || 'Untitled Design'}"? This cannot be undone.`,
    );
    if (!confirmed) return;
    setDeletingDesignId(design.id);
    try {
      await firebase.db
        .collection('franchisees')
        .doc(franchiseeId)
        .collection('studioProjects')
        .doc(design.id)
        .delete();
    } catch (error) {
      console.error('Failed to delete studio design:', error);
      setCreateError('Unable to delete design. Please try again.');
      if (process.env.NODE_ENV !== 'production') {
        setDebugError(error?.message || 'Unknown error');
      }
    } finally {
      setDeletingDesignId('');
    }
  };

  useEffect(() => {
    if (!firebase || !uid || !franchiseeId) {
      setRecentDesigns([]);
      return undefined;
    }
    setRecentLoading(true);

    const query = firebase.db
      .collection('franchisees')
      .doc(franchiseeId)
      .collection('studioProjects')
      .orderBy('updatedAt', 'desc')
      .limit(12);

    const unsubscribe = query.onSnapshot(
      (snapshot) => {
        let totalBytes = 0;
        const items = snapshot.docs.map((doc) => {
          const data = doc.data() || {};
          totalBytes += measureJsonBytes(data);
          const updatedAtValue =
            data.updatedAt?.toDate?.() ||
            (typeof data.updatedAt === 'string' ? new Date(data.updatedAt) : null) ||
            data.createdAt?.toDate?.() ||
            (typeof data.createdAt === 'string' ? new Date(data.createdAt) : null) ||
            doc.updateTime?.toDate?.() ||
            doc.createTime?.toDate?.() ||
            null;
          const updatedAt =
            updatedAtValue instanceof Date && !Number.isNaN(updatedAtValue.getTime())
              ? updatedAtValue.toISOString()
              : '';
          const updatedAtMs =
            updatedAtValue instanceof Date && !Number.isNaN(updatedAtValue.getTime())
              ? updatedAtValue.getTime()
              : 0;
          return {
            id: doc.id,
            name: data.name || 'Untitled Design',
            width: Number(data.width || 0) || undefined,
            height: Number(data.height || 0) || undefined,
            unit: data.unit || 'px',
            updatedAt,
            updatedAtMs,
          };
        });
        items.sort((a, b) => b.updatedAtMs - a.updatedAtMs);
        setRecentDesigns(items);
        setStorageUsedBytes(totalBytes);
        setRecentLoading(false);
      },
      (error) => {
        console.error('Failed to load recent designs from Firebase:', error);
        setRecentDesigns([]);
        setStorageUsedBytes(0);
        setRecentLoading(false);
      },
    );

    return () => unsubscribe();
  }, [firebase, uid, franchiseeId]);

  const openDesignNameModal = (preset) => {
    const baseLabel = preset.title || preset.label || 'Design';
    const defaultName = `${baseLabel} - ${formatDateLabel(new Date())}`;
    setDesignName(defaultName);
    setDesignNameError('');
    setPendingPreset(preset);
  };

  const closeDesignNameModal = () => {
    if (creatingPresetId) return;
    setPendingPreset(null);
    setDesignNameError('');
  };

  const filteredPresets = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return STUDIO_PRESETS.filter((preset) => {
      const matchesCategory =
        activeCategory === 'All' || preset.category === activeCategory;
      if (!matchesCategory) {
        return false;
      }
      if (!normalized) {
        return true;
      }
      const haystack = [
        preset.title,
        preset.label,
        preset.category,
        preset.id,
        ...(preset.tags || []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalized);
    });
  }, [activeCategory, query]);

  const handleCreateDesign = async (preset) => {
    if (!authUser || !firebase) {
      setCreateError('You must be signed in to create a design.');
      return;
    }

    if (!franchiseeId || !uid) {
      setCreateError('Unable to create design. Please try again.');
      return;
    }

    openDesignNameModal(preset);
  };

  const handleOpenCustomModal = () => {
    setCustomErrors({});
    setCreateError('');
    setCustomModalOpen(true);
    if (!customWidth || !customHeight) {
      setCustomWidth('');
      setCustomHeight('');
      setCustomName('');
      setCustomNameTouched(false);
    }
  };

  const closeCustomModal = () => {
    if (creatingPresetId) return;
    setCustomModalOpen(false);
    setCustomErrors({});
  };

  const buildCustomName = (widthValue, heightValue) =>
    `Custom - ${widthValue}×${heightValue} - ${formatDateLabel(new Date())}`;

  const validateCustomSize = (widthValue, heightValue) => {
    const errors = {};
    const parsedWidth = Math.round(Number(widthValue));
    const parsedHeight = Math.round(Number(heightValue));

    if (!widthValue) {
      errors.width = 'Width is required.';
    } else if (Number.isNaN(parsedWidth)) {
      errors.width = 'Width must be a number.';
    } else if (parsedWidth < 100) {
      errors.width = 'Minimum width is 100px.';
    } else if (parsedWidth > 8000) {
      errors.width = 'Maximum width is 8000px.';
    }

    if (!heightValue) {
      errors.height = 'Height is required.';
    } else if (Number.isNaN(parsedHeight)) {
      errors.height = 'Height must be a number.';
    } else if (parsedHeight < 100) {
      errors.height = 'Minimum height is 100px.';
    } else if (parsedHeight > 8000) {
      errors.height = 'Maximum height is 8000px.';
    }

    return {
      errors,
      parsedWidth: Number.isNaN(parsedWidth) ? null : parsedWidth,
      parsedHeight: Number.isNaN(parsedHeight) ? null : parsedHeight,
    };
  };

  const handleCustomDimensionChange = (field, value) => {
    if (field === 'width') {
      setCustomWidth(value);
    } else {
      setCustomHeight(value);
    }

    const nextWidth = field === 'width' ? value : customWidth;
    const nextHeight = field === 'height' ? value : customHeight;
    const { errors } = validateCustomSize(nextWidth, nextHeight);
    setCustomErrors(errors);

    if (!customNameTouched && value) {
      if (nextWidth && nextHeight) {
        setCustomName(buildCustomName(nextWidth, nextHeight));
      }
    }
  };

  const handleCustomNameChange = (value) => {
    setCustomNameTouched(true);
    setCustomName(value);
    if (customErrors.name && value.trim()) {
      setCustomErrors((prev) => ({ ...prev, name: '' }));
    }
  };

  const handleCustomQuickSize = (size) => {
    setCustomWidth(String(size.width));
    setCustomHeight(String(size.height));
    const { errors } = validateCustomSize(size.width, size.height);
    setCustomErrors(errors);
    if (!customNameTouched) {
      setCustomName(buildCustomName(size.width, size.height));
    }
  };

  const handleCreateCustomDesign = async () => {
    if (!authUser || !firebase) {
      setCreateError('You must be signed in to create a design.');
      return;
    }
    if (!franchiseeId || !uid) {
      setCreateError('Unable to create design. Please try again.');
      return;
    }

    const { errors, parsedWidth, parsedHeight } = validateCustomSize(
      customWidth,
      customHeight,
    );

    if (Object.keys(errors).length > 0) {
      setCustomErrors(errors);
      return;
    }

    const trimmedName = customName.trim();
    if (!trimmedName) {
      setCustomErrors({
        ...errors,
        name: 'Please enter a design name.',
      });
      return;
    }

    setCreatingPresetId('custom-size');
    setCustomErrors({});

    try {
      setDebugError('');
      if (storageUsedBytes >= STORAGE_LIMIT_BYTES) {
        setCreateError('Storage limit reached (1GB). Please delete older designs.');
        return;
      }
      const projectRef = firebase.db
        .collection('franchisees')
        .doc(franchiseeId)
        .collection('studioProjects')
        .doc();
      const pageId = 'page-1';

      await createProject(firebase, franchiseeId, projectRef.id, {
        name: trimmedName,
        width: parsedWidth,
        height: parsedHeight,
        unit: customUnit || 'px',
        isPrint: false,
        franchiseeId,
        createdBy: uid,
        createdAt: firebase.fieldValue.serverTimestamp(),
        updatedAt: firebase.fieldValue.serverTimestamp(),
        lastOpenedAt: firebase.fieldValue.serverTimestamp(),
        thumbnailUrl: '',
        pages: [{ id: pageId, elements: [] }],
        activePageId: pageId,
        version: 1,
      });

      setCustomModalOpen(false);
      setCustomNameTouched(false);
      openEditorInNewTab(projectRef.id);
    } catch (error) {
      console.error('Failed to create custom studio project:', error);
      setCreateError('Unable to create design. Please try again.');
      if (process.env.NODE_ENV !== 'production') {
        setDebugError(error?.message || 'Unknown error');
      }
    } finally {
      setCreatingPresetId(null);
    }
  };

  const confirmCreateDesign = async () => {
    if (!pendingPreset) return;
    const trimmedName = designName.trim();
    if (!trimmedName) {
      setDesignNameError('Please enter a design name.');
      return;
    }
    setCreateError('');
    setCreatingPresetId(pendingPreset.id);

    try {
      setDebugError('');
      if (storageUsedBytes >= STORAGE_LIMIT_BYTES) {
        setCreateError('Storage limit reached (1GB). Please delete older designs.');
        return;
      }
      const projectRef = firebase.db
        .collection('franchisees')
        .doc(franchiseeId)
        .collection('studioProjects')
        .doc();
      const pageId = 'page-1';

      await createProject(firebase, franchiseeId, projectRef.id, {
        name: trimmedName,
        width: pendingPreset.width,
        height: pendingPreset.height,
        unit: pendingPreset.unit || 'px',
        isPrint: Boolean(pendingPreset.isPrint),
        franchiseeId,
        createdBy: uid,
        createdAt: firebase.fieldValue.serverTimestamp(),
        updatedAt: firebase.fieldValue.serverTimestamp(),
        lastOpenedAt: firebase.fieldValue.serverTimestamp(),
        thumbnailUrl: '',
        pages: [{ id: pageId, elements: [] }],
        activePageId: pageId,
        version: 1,
      });

      setPendingPreset(null);
      setDesignNameError('');
      openEditorInNewTab(projectRef.id);
    } catch (error) {
      console.error('Failed to create studio project:', error);
      setCreateError('Unable to create design. Please try again.');
      setDesignNameError('Unable to create design. Please try again.');
      if (process.env.NODE_ENV !== 'production') {
        setDebugError(error?.message || 'Unknown error');
      }
    } finally {
      setCreatingPresetId(null);
    }
  };

  const customCanSubmit = Boolean(
    customWidth &&
      customHeight &&
      customName.trim() &&
      !customErrors.width &&
      !customErrors.height &&
      !customErrors.name,
  );

  return (
    <StudioPage
      hideLeftSidebar
      topContent={(
        <div className="studio-home-topbar">
          <div className="studio-search">
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search sizes (Instagram, WhatsApp, A4...)"
              aria-label="Search sizes"
            />
          </div>
          <div className="studio-storage">
            <div className="studio-storage-label">Storage</div>
            <div className="studio-storage-bar">
              <div
                className="studio-storage-fill"
                style={{
                  width: `${Math.min(
                    100,
                    (storageUsedBytes / STORAGE_LIMIT_BYTES) * 100,
                  ).toFixed(2)}%`,
                }}
              />
            </div>
            <div className="studio-storage-meta">
              {formatBytes(storageUsedBytes)} / 1 GB
            </div>
          </div>
        </div>
      )}
      rightContent={(
        <div className="studio-home-recents">
          <div className="studio-sidebar-title">Recent Designs</div>
          {recentLoading ? (
            <div className="studio-sidebar-placeholder">Loading designs...</div>
          ) : recentDesigns.length === 0 ? (
            <div className="studio-sidebar-placeholder">No recent designs yet.</div>
          ) : (
            <div className="studio-recents-list">
              {recentDesigns.map((item) => (
                <div key={item.id} className="studio-recent-item">
                  <a
                    href={`/studio-editor/${item.id}`}
                    className="studio-recent-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="studio-recent-title">{item.name || 'Untitled Design'}</div>
                    <div className="studio-recent-meta">
                      {item.width} x {item.height} {item.unit || 'px'}
                    </div>
                  </a>
                  <div className="studio-recent-actions">
                    <button
                      type="button"
                      className="studio-recent-delete"
                      onClick={(event) => handleDeleteDesign(event, item)}
                      disabled={deletingDesignId === item.id}
                    >
                      {deletingDesignId === item.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    >
      <div className="studio-home">
        <div className="studio-home-header">
          <div className="studio-home-title-row">
            <h2>Create Design</h2>
            <span className="studio-beta-badge">Beta</span>
          </div>
          <p>Pick a preset to open the editor instantly.</p>
          <div className="studio-home-beta-note">
            Studio is in beta. Some tools and templates may change as we finalize the experience.
          </div>
          <div className="studio-home-note">
            Editor opens in a new tab for distraction-free designing.
            <span
              className="studio-home-tip"
              title="Your editor opens separately so the dashboard stays uncluttered."
            >
              i
            </span>
          </div>
        </div>

        <div className="studio-filter-chips" role="tablist" aria-label="Preset categories">
          {CATEGORY_OPTIONS.map((category) => (
            <button
              key={category}
              type="button"
              className={`studio-filter-chip ${
                activeCategory === category ? 'active' : ''
              }`}
              onClick={() => setActiveCategory(category)}
              aria-pressed={activeCategory === category}
            >
              {category}
            </button>
          ))}
        </div>

        {createError && <div className="studio-home-error">{createError}</div>}
        {openNotice && (
          <div className="studio-home-notice">
            <span>{openNotice}</span>
            {blockedUrl && (
              <a
                className="studio-home-open-link"
                href={blockedUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open editor
              </a>
            )}
          </div>
        )}
        {debugError && process.env.NODE_ENV !== 'production' && (
          <details className="studio-debug-panel">
            <summary>Show error details</summary>
            <div>{debugError}</div>
          </details>
        )}

        {filteredPresets.length === 0 && (
          <div className="studio-home-empty">No presets match this search.</div>
        )}
        <div className="studio-presets-grid">
          {filteredPresets.map((preset) => {
            const disabled = Boolean(creatingPresetId) || workspaceLoading;
            const label = preset.title || preset.label;
            return (
              <button
                key={preset.id}
                type="button"
                className="studio-preset-card"
                onClick={() => handleCreateDesign(preset)}
                disabled={disabled}
              >
                <div className="studio-preset-title">{label}</div>
                <div className="studio-preset-meta">
                  {preset.width} x {preset.height} {preset.unit || 'px'}
                </div>
                {preset.category && (
                  <div className="studio-preset-category">{preset.category}</div>
                )}
                {preset.isPrint && (
                  <span className="studio-preset-tag">Print</span>
                )}
                {creatingPresetId === preset.id && (
                  <div className="studio-preset-status">Creating...</div>
                )}
              </button>
            );
          })}
          <button
            type="button"
            className="studio-preset-card studio-custom-card"
            onClick={handleOpenCustomModal}
            disabled={Boolean(creatingPresetId) || workspaceLoading}
          >
            <div className="studio-custom-icon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="studio-preset-title">Custom Size</div>
            <div className="studio-preset-meta">Create your own dimensions</div>
          </button>
        </div>
      </div>
      <DesignNameModal
        isOpen={Boolean(pendingPreset)}
        title="Create Design"
        message="Enter a name for your design."
        value={designName}
        onChange={setDesignName}
        onConfirm={confirmCreateDesign}
        onCancel={closeDesignNameModal}
        confirmText="Create"
        cancelText="Cancel"
        errorMessage={designNameError}
        loading={Boolean(creatingPresetId)}
      />
      <CustomSizeModal
        isOpen={customModalOpen}
        loading={Boolean(creatingPresetId)}
        canSubmit={customCanSubmit}
        widthValue={customWidth}
        heightValue={customHeight}
        unitValue={customUnit}
        nameValue={customName}
        errors={customErrors}
        quickSizes={QUICK_SIZES}
        onWidthChange={(value) => handleCustomDimensionChange('width', value)}
        onHeightChange={(value) => handleCustomDimensionChange('height', value)}
        onUnitChange={setCustomUnit}
        onNameChange={handleCustomNameChange}
        onQuickSize={handleCustomQuickSize}
        onCancel={closeCustomModal}
        onConfirm={handleCreateCustomDesign}
      />
    </StudioPage>
  );
};

export default StudioHome;
